import { createAsyncThunk } from "@reduxjs/toolkit";
import { CommonRootState } from "@common/types/common-root-state-type";
import { WaNote } from "@common-models/WaNote";
import { Task } from "@common-models/task";
import { Board } from "@common-models/board";
import { TasksGroup } from "@common-models/task-group";
import { DBWaNoteThunks, DBBoardsThunks, DBTaskgroupsThunks, DBTasksThunks, DBTasksExtraDataThunks, boardsSelectByQuery, taskGroupsSelectByQuery, tasksSelectByQueryOnlyMaxMin } from "@common-reducers/DBServiceThunks";
import { ExtraDataItem } from "@common-models/ExtraDataItem";
import { selectUserId, selectCurrentWorkspace } from "@common-reducers/UserSelectors";
import { btLayoutSidebarActions } from "@common-reducers/BtLayoutSidebarReducer";
import { boardsActions } from "@common-reducers/BoardsReducer";
import { subscriptionPopupActions } from "@common-reducers/SubscriptionPopupReducer";
import { namespace } from "@common-config/constants";
import i18n from "i18next";
import { toast } from "sonner";
import type { Descendant } from "slate";

const translate = (key: string, options?: Record<string, any>) =>
    i18n.t(key, { ns: namespace, ...options });

export interface CreateNoteFromMessageParams {
    chatId: string;
    messageText: string;
    messageId?: string;
    imageUrls?: string[];
    videoUrls?: string[];
    replyQuote?: string;
}

export interface CreateTaskFromMessageParams {
    chatId: string;
    chatName: string;
    messageText: string;
    messageId?: string;
    imageUrls?: string[];
    videoUrls?: string[];
    replyQuote?: string;
}

/**
 * Build a slate document containing only the media side of a source message
 * (images + videos + optional reply quote). Used as the description body
 * for tasks (where the text becomes the task title) and as a baseline for
 * reminders / notes where we want media in the rich-text body.
 *
 * Returns null when there is no media — caller decides whether to skip
 * creating the description or to fall back to a plain paragraph.
 */
function buildMediaSlateForMessage(params: {
    imageUrls?: string[];
    videoUrls?: string[];
    replyQuote?: string;
}): Descendant[] | null {
    const trimmedQuote = (params.replyQuote || '').trim();
    const validImageUrls = Array.isArray(params.imageUrls) ? params.imageUrls.filter((u): u is string => typeof u === 'string' && u.length > 0) : [];
    const validVideoUrls = Array.isArray(params.videoUrls) ? params.videoUrls.filter((u): u is string => typeof u === 'string' && u.length > 0) : [];

    if (!trimmedQuote && validImageUrls.length === 0 && validVideoUrls.length === 0) {
        return null;
    }

    const blocks: Descendant[] = [];
    if (trimmedQuote) {
        blocks.push({ type: 'block-quote', children: [{ text: trimmedQuote }] } as Descendant);
    }
    const pushVoid = (node: Descendant) => {
        blocks.push(node);
        // Slate requires a non-void block after a void node.
        blocks.push({ type: 'paragraph', children: [{ text: '' }] } as Descendant);
    };
    for (const url of validImageUrls) pushVoid({ type: 'image', url, children: [{ text: '' }] } as Descendant);
    for (const url of validVideoUrls) pushVoid({ type: 'video', url, children: [{ text: '' }] } as Descendant);
    return blocks;
}

/**
 * Build a meaningful task title/text when the source message has no text
 * (e.g. image-only, video-only, sticker, or a reply with caption stripped).
 * Order of preference: explicit text → reply quote → media-type marker.
 */
function buildTaskTextFromMessage(params: {
    messageText?: string;
    imageUrls?: string[];
    videoUrls?: string[];
    replyQuote?: string;
}): string {
    const trimmed = (params.messageText || '').trim();
    if (trimmed) return trimmed;

    const quote = (params.replyQuote || '').trim();
    if (quote) return `↩ ${quote}`;

    const hasImg = (params.imageUrls?.length ?? 0) > 0;
    const hasVid = (params.videoUrls?.length ?? 0) > 0;
    if (hasImg && hasVid) return '[Image + Video]';
    if (hasImg) return params.imageUrls!.length > 1 ? `[${params.imageUrls!.length} Images]` : '[Image]';
    if (hasVid) return '[Video]';

    return 'New Task';
}

// Create a note from a message
export const createNoteFromMessageThunk = createAsyncThunk<
    void,
    CreateNoteFromMessageParams,
    { state: CommonRootState }
>("messageActions/createNoteFromMessage", async ({ chatId, messageText, messageId, imageUrls, videoUrls, replyQuote }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState();
    const userId = selectUserId(state);

    if (!userId || !chatId) {
        console.error('[message-actions] Cannot create note: missing userId or chatId');
        return;
    }

    // Build slate content combining quote + text + media so a note created
    // from a non-text message (image / video / reply) doesn't end up empty.
    const trimmedText = (messageText || '').trim();
    const rawQuote = (replyQuote || '').trim();
    // Drop the quote if it's the same content as (or contained in) the
    // message body — extractor false positives produced notes where the
    // body text appeared once as a block-quote and again as the paragraph.
    const trimmedQuote =
        rawQuote && rawQuote !== trimmedText && !trimmedText.includes(rawQuote)
            ? rawQuote
            : '';
    const validImageUrls = Array.isArray(imageUrls) ? imageUrls.filter((u): u is string => typeof u === 'string' && u.length > 0) : [];
    const validVideoUrls = Array.isArray(videoUrls) ? videoUrls.filter((u): u is string => typeof u === 'string' && u.length > 0) : [];

    const slateContent: Descendant[] = [];

    if (trimmedQuote) {
        // Render the quoted message as a block-quote so it's visually
        // distinct from the note body.
        slateContent.push({
            type: 'block-quote',
            children: [{ text: trimmedQuote }],
        } as Descendant);
    }

    if (trimmedText) {
        slateContent.push({
            type: 'paragraph',
            children: [{ text: trimmedText }],
        } as Descendant);
    }

    const pushVoidWithFollowup = (node: Descendant) => {
        slateContent.push(node);
        // Slate requires a non-void block after a void node; add an empty
        // paragraph so the editor stays in a valid state and the next
        // media is on its own line.
        slateContent.push({
            type: 'paragraph',
            children: [{ text: '' }],
        } as Descendant);
    };

    for (const url of validImageUrls) {
        pushVoidWithFollowup({
            type: 'image',
            url,
            children: [{ text: '' }],
        } as Descendant);
    }

    for (const url of validVideoUrls) {
        pushVoidWithFollowup({
            type: 'video',
            url,
            children: [{ text: '' }],
        } as Descendant);
    }

    // Source message had neither text nor media we could extract — leave a
    // placeholder paragraph rather than persisting a truly empty note.
    if (slateContent.length === 0) {
        slateContent.push({
            type: 'paragraph',
            children: [{ text: '' }],
        } as Descendant);
    }

    const newNote: WaNote = {
        chatId,
        extraData: slateContent,
        owner: userId,
        createdAt: new Date().toISOString(),
    };

    try {
        await dispatch(DBWaNoteThunks.create(newNote)).unwrap();

        // Open the Notes sidebar - optimistic update handles the rest
        dispatch(btLayoutSidebarActions.openWithComponent('Notes'));

        toast.success('Note created');
    } catch (error: any) {
        // Check if error is due to CRM limit reached
        if (error?.message?.includes('total notes limit')) {
            dispatch(subscriptionPopupActions.openPopup('notes'));
            toast.error('You\'ve reached your notes limit. Upgrade to get more notes.');
        } else if (error?.message?.includes('notes limit for this contact')) {
            dispatch(subscriptionPopupActions.openPopup('notes per contact'));
            toast.error('You\'ve reached your notes limit for this contact.');
        } else {
            console.error('[message-actions] Error creating note:', error);
            toast.error('Failed to create note');
        }
    }
});

// Create a task from a message (will auto-create board if needed)
export const createTaskFromMessageThunk = createAsyncThunk<
    void,
    CreateTaskFromMessageParams,
    { state: CommonRootState }
>("messageActions/createTaskFromMessage", async ({ chatId, chatName, messageText, messageId, imageUrls, videoUrls, replyQuote }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState();
    const userId = selectUserId(state);
    const workspace = selectCurrentWorkspace(state);

    if (!userId || !chatId) {
        console.error('[message-actions] Cannot create task: missing userId or chatId');
        return;
    }

    const resolvedTaskText = buildTaskTextFromMessage({ messageText, imageUrls, videoUrls, replyQuote });

    // 1. Check if board exists for this chat (using _IdByWaChatIds which contains the chat ID)
    // Never a deleted board: it is invisible to the user, so a task put there is lost.
    let boards = boardsSelectByQuery(state.DBBoardsReducer, { isWABoard: true, deleted: { $ne: true } });
    let board: Board | undefined = boards.find(b => b._IdByWaChatIds?.includes(chatId));

    // 2. If no board exists, create one
    if (!board) {
        console.log('[message-actions] No board found for chat, creating new board');

        const newBoard: Partial<Board> = {
            name: chatName || 'Tasks',
            titleIcon: '📋',
            owner: userId,
            workspaceId: workspace?._id?.toString(),
            isWABoard: true,
            _IdByWaChatIds: [chatId],
        };

        // #1136: creates REJECT with a typed DBCreateError when nothing was stored.
        const boardRes = await dispatch(DBBoardsThunks.create(newBoard));
        const createdBoard = DBBoardsThunks.create.fulfilled.match(boardRes) ? boardRes.payload : undefined;
        const boardId = createdBoard?._id;

        if (!boardId) {
            console.error('[message-actions] Failed to create board');
            toast.error('Failed to create task board');
            return;
        }

        // Create default group for the board
        const defaultGroup: Partial<TasksGroup> = {
            name: 'Tasks',
            boardId,
            order: 0,
            colorAccent: '#4CAF50',
            owner: userId,
        };

        const groupRes = await dispatch(DBTaskgroupsThunks.create(defaultGroup));

        // Check if group creation failed
        if (DBTaskgroupsThunks.create.rejected.match(groupRes)) {
            console.error('[message-actions] Failed to create default group for new board:', groupRes.payload?.message ?? groupRes.error.message);
            toast.error('Failed to create task group');
            return;
        }

        // Use the created board directly from the response
        board = createdBoard;

        // Get the group from the response for creating the task
        const createdGroup = groupRes.payload;
        if (createdGroup?._id) {
            // Create the task directly with the new board and group
            const taskMaxOrder = 0;
            const newTask: Task = {
                text: resolvedTaskText,
                boardId: boardId,
                groupId: createdGroup._id,
                owner: userId,
                order: taskMaxOrder + 1,
                chatId,
            };

            const taskRes = await dispatch(DBTasksThunks.create(newTask));
            if (DBTasksThunks.create.rejected.match(taskRes)) {
                console.error('[message-actions] Failed to create task:', taskRes.payload?.message ?? taskRes.error.message);
                toast.error('Failed to create task');
                return;
            }

            // If the source message had media (image / video / reply quote),
            // persist it as the task's first update so the user sees the
            // image/video in the Updates panel of the task.
            const mediaBlocks = buildMediaSlateForMessage({ imageUrls, videoUrls, replyQuote });
            const createdTaskId = taskRes.payload._id;
            if (mediaBlocks && createdTaskId) {
                const extraDataItem: ExtraDataItem = {
                    taskId: createdTaskId,
                    groupId: createdGroup._id,
                    boardId: boardId,
                    author: userId,
                    extraData: mediaBlocks,
                    isUpdating: false,
                    isPinedToTop: false,
                };
                dispatch(DBTasksExtraDataThunks.create(extraDataItem));
            }

            // Set selected board and open Tasks sidebar
            dispatch(boardsActions.privateSetSelectedBoardId(boardId));
            dispatch(btLayoutSidebarActions.openWithComponent('Tasks'));

            toast.success('Task created');
            return;
        }
    }

    if (!board?._id) {
        console.error('[message-actions] Board not found after creation');
        toast.error('Failed to create task');
        return;
    }

    // 3. Get or create a group for the task
    const refreshedState = thunkAPI.getState();
    let groups = taskGroupsSelectByQuery(refreshedState.DBTaskgroupsReducer, { boardId: board._id });
    let group = groups[0];

    if (!group) {
        console.log('[message-actions] No group found, creating default group for board:', board._id);

        // Ensure boardId is valid before creating group
        if (!board._id) {
            console.error('[message-actions] Board._id is missing');
            toast.error('Failed to create task: Invalid board');
            return;
        }

        // Create default group
        const defaultGroup: Partial<TasksGroup> = {
            name: 'Tasks',
            boardId: board._id,
            order: 0,
            colorAccent: '#4CAF50',
            owner: userId,
        };

        const groupRes = await dispatch(DBTaskgroupsThunks.create(defaultGroup));

        // Check if group creation failed
        if (DBTaskgroupsThunks.create.rejected.match(groupRes)) {
            console.error('[message-actions] Failed to create group:', groupRes.payload?.message ?? groupRes.error.message);
            console.error('[message-actions] Board details:', { boardId: board._id, boardType: typeof board._id });
            toast.error('Failed to create task group');
            return;
        }

        group = groupRes.payload;
    }

    if (!group?._id) {
        console.error('[message-actions] Group not found after creation attempt');
        toast.error('Failed to create task');
        return;
    }

    // 4. Get max order for tasks in this group
    const taskMaxOrder = tasksSelectByQueryOnlyMaxMin(
        refreshedState.DBTasksReducer,
        { groupId: group._id, deleted: false },
        "order",
        "max"
    ) ?? 0;

    // 5. Create the task
    const newTask: Task = {
        text: resolvedTaskText,
        boardId: board._id,
        groupId: group._id,
        owner: userId,
        order: taskMaxOrder + 1,
        chatId,
    };

    const taskRes = await dispatch(DBTasksThunks.create(newTask));
    if (DBTasksThunks.create.rejected.match(taskRes)) {
        console.error('[message-actions] Failed to create task:', taskRes.payload?.message ?? taskRes.error.message);
        toast.error('Failed to create task');
        return;
    }

    // If the source message had media (image / video / reply quote),
    // persist it as the task's first update so the user sees the image /
    // video in the Updates panel of the task.
    const mediaBlocks = buildMediaSlateForMessage({ imageUrls, videoUrls, replyQuote });
    const createdTaskId = taskRes.payload._id;
    if (mediaBlocks && createdTaskId) {
        const extraDataItem: ExtraDataItem = {
            taskId: createdTaskId,
            groupId: group._id,
            boardId: board._id,
            author: userId,
            extraData: mediaBlocks,
            isUpdating: false,
            isPinedToTop: false,
        };
        dispatch(DBTasksExtraDataThunks.create(extraDataItem));
    }

    // 6. Set selected board and open Tasks sidebar - optimistic update handles the rest
    dispatch(boardsActions.privateSetSelectedBoardId(board._id));
    dispatch(btLayoutSidebarActions.openWithComponent('Tasks'));

    toast.success('Task created');
});
